import React from 'react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * Tiny grey label over a bold value — the atom the reference's detail panel is
 * built from. Two of these side by side make the two-column grid you see on an
 * order or a booking.
 */

export interface FieldProps {
  label: React.ReactNode;
  value?: React.ReactNode;
  /** Rendered instead of `value` when you need a control or a badge there. */
  children?: React.ReactNode;
  /** Span both columns inside a `FieldGrid`. */
  wide?: boolean;
  className?: string;
}

const Field: React.FC<FieldProps> = ({ label, value, children, wide, className }) => (
  <div className={cn('min-w-0', wide && 'col-span-2', className)}>
    <Text variant="fieldLabel" tone="tertiary" as="p" className="mb-1">
      {label}
    </Text>
    {children ?? (
      <Text variant="bodySemi" as="p" className="break-words">
        {/* An empty value still needs to occupy its line, or the two columns
            fall out of alignment down the panel. */}
        {value === undefined || value === null || value === '' ? '—' : value}
      </Text>
    )}
  </div>
);

export const FieldGrid: React.FC<{
  columns?: 1 | 2 | 3;
  className?: string;
  children: React.ReactNode;
}> = ({ columns = 2, className, children }) => (
  <div
    className={cn(
      'grid gap-x-4 gap-y-4',
      columns === 1 ? 'grid-cols-1' : columns === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2',
      className,
    )}
  >
    {children}
  </div>
);

export default React.memo(Field);
